import { COMPANY, WHATSAPP_URL } from "@/lib/company";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Building2, Globe2, LineChart, LockKeyhole, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FIT_REVIEW_NOTE } from "@/lib/pricing";
import { SiteFooter, SiteHeader } from "@/components/layout/SiteChrome";

const BASE_URL = "https://adkiro.com";
const PAGE_URL = `${BASE_URL}/about`;
const TITLE = "Kristian Kinchev, Founder of Adkiro | Meta Ads for Property";
const DESCRIPTION =
  "Meet Kristian Kinchev, founder of Adkiro: founder-led Meta advertising for property agencies, brokers and developers in Dubai, Europe and the US.";

const PRINCIPLES = [
  {
    number: "01",
    icon: ShieldCheck,
    title: "Ownership stays with the client",
    copy: "Your company retains every Meta account, dataset and creative asset. Adkiro works through controlled partner access with only the permissions required.",
  },
  {
    number: "02",
    icon: LineChart,
    title: "Decisions connect to the business",
    copy: "Campaigns are judged by the enquiries your team would actually call back and the viewings that follow—not isolated platform metrics.",
  },
  {
    number: "03",
    icon: LockKeyhole,
    title: "Discretion is part of the work",
    copy: "Briefs, budgets, account data and growth plans are handled privately. Confidential engagements and NDA-based collaboration are welcome.",
  },
];

const OPERATING_MODEL = [
  {
    number: "01",
    title: "Understand the property and the buyer",
    copy: "Clarify the listing or project, its price, the market, the likely buyer and how your team follows up enquiries.",
  },
  {
    number: "02",
    title: "Establish a reliable signal",
    copy: "Review account structure, measurement and creative evidence before changing budgets or introducing new campaigns.",
  },
  {
    number: "03",
    title: "Operate with controlled intent",
    copy: "Test deliberately, document the rationale and scale only when the commercial evidence supports the next decision.",
  },
];

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "profile" },
      { property: "og:image", content: `${BASE_URL}/logo.png?v=20260907` },
      { property: "og:image:alt", content: "Adkiro" },
      { name: "twitter:image", content: `${BASE_URL}/logo.png?v=20260907` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "AboutPage",
              "@id": `${PAGE_URL}#page`,
              url: PAGE_URL,
              name: TITLE,
              description: DESCRIPTION,
              about: { "@id": `${PAGE_URL}#kristian-kinchev` },
              isPartOf: { "@id": `${BASE_URL}/#website` },
            },
            {
              "@type": "Person",
              "@id": `${PAGE_URL}#kristian-kinchev`,
              name: "Kristian Kinchev",
              url: PAGE_URL,
              worksFor: { "@id": `${BASE_URL}/#organization` },
              knowsAbout: [
                "Meta Ads management",
                "Facebook advertising",
                "Instagram advertising",
                "Meta Pixel",
                "Conversions API",
              ],
              homeLocation: {
                "@type": "Place",
                name: "Sofia, Bulgaria",
              },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: `${BASE_URL}/`,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "About",
                  item: PAGE_URL,
                },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <SiteHeader tone="ink" />

      <main>
        <section data-tone="ink" className="tone-ink about-hero relative overflow-hidden">
          <div className="about-hero__index" aria-hidden="true">
            01
          </div>
          <div className="relative mx-auto grid max-w-7xl gap-14 px-5 pb-16 pt-16 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:px-8 lg:pb-24 lg:pt-24">
            <div className="hero-copy max-w-4xl">
              <div className="hero-eyebrow">
                <span className="hero-eyebrow__line" />
                <span>Founder / Adkiro / Sofia</span>
              </div>
              <h1 className="mt-8 text-[clamp(3.4rem,7.2vw,7.4rem)] leading-[0.87]">
                Senior judgement.
                <span className="mt-2 block font-display italic text-primary">
                  Direct accountability.
                </span>
              </h1>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                Kristian Kinchev leads Adkiro as a founder-led Meta advertising practice for
                property: agencies, brokers and developers who want one accountable senior partner.
                He also advertises listings for his own property business in Sofia, so every method
                is tried on his own budget first.
              </p>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button variant="brand" size="xl" asChild>
                  <a href="/#audit">
                    Get a free review <ArrowRight className="size-4" />
                  </a>
                </Button>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-3 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  Message on WhatsApp
                </a>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">{FIT_REVIEW_NOTE}</p>
            </div>

            <aside className="about-principle hero-visual" aria-label="Adkiro founder principle">
              <div className="about-principle__topline">
                <span>Founder principle</span>
                <span>KK / 01</span>
              </div>
              <div className="about-principle__mark" aria-hidden="true">
                <img src="/logo-mark.svg?v=20260907" alt="" width="112" height="118" />
              </div>
              <div className="about-principle__statement">
                <p>
                  Advertising should create <em>clarity</em> at the level of the business—not more
                  noise inside an ad account.
                </p>
              </div>
              <div className="about-principle__identity">
                <div>
                  <strong>Kristian Kinchev</strong>
                  <span>Founder, Adkiro</span>
                </div>
                <div className="text-right">
                  <strong>Sofia / Europe</strong>
                  <span>International delivery</span>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <div className="about-credentials border-b border-border">
          <div className="mx-auto grid max-w-7xl grid-cols-2 px-5 md:grid-cols-4 lg:px-8">
            {[
              ["01", "Founder-led"],
              ["02", "Confidential by default"],
              ["03", "Client-owned assets"],
              ["04", "Dubai · Europe · US"],
            ].map(([number, label]) => (
              <div key={number} className="about-credentials__item">
                <span>{number}</span>
                <strong>{label}</strong>
              </div>
            ))}
          </div>
        </div>

        <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-[0.52fr_1fr] lg:gap-24">
            <div>
              <p className="section-kicker">02 / The practice</p>
              <h2 className="mt-5 text-4xl leading-[0.96] sm:text-6xl">
                Deliberately focused.
                <span className="block italic text-primary">Personally accountable.</span>
              </h2>
            </div>
            <div className="max-w-3xl lg:pt-9">
              <p className="font-display text-2xl leading-snug sm:text-3xl">
                Adkiro is designed for property teams who want experienced attention on the
                account—not layers of salespeople, coordinators and disconnected reporting.
              </p>
              <div className="mt-9 grid gap-7 text-sm leading-relaxed text-muted-foreground sm:grid-cols-2">
                <p>
                  Kristian remains close to the work across campaign structure, measurement,
                  creative testing and budget decisions. The goal is a clear operating rationale
                  that your leadership team can understand and challenge.
                </p>
                <p>
                  The practice is operated by {COMPANY.legalName} in Sofia and works in English with
                  agencies, brokers and developers in Dubai, Europe and the United States.
                </p>
              </div>
              <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4 border-y border-border py-5 text-[0.65rem] uppercase tracking-[0.13em] text-muted-foreground">
                <span className="inline-flex items-center gap-2">
                  <Building2 className="size-4 text-primary" /> {COMPANY.legalName}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Globe2 className="size-4 text-primary" /> Sofia · Europe · International
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-border bg-surface">
          <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28">
            <div className="mb-14 grid gap-8 lg:grid-cols-[0.35fr_1fr]">
              <p className="section-kicker">03 / Standards</p>
              <h2 className="max-w-4xl text-4xl leading-[0.96] sm:text-6xl">
                The standards behind every engagement
              </h2>
            </div>
            <div className="grid border-l border-t border-border md:grid-cols-3">
              {PRINCIPLES.map((principle) => (
                <article
                  key={principle.number}
                  className="about-standard border-b border-r border-border p-7 sm:p-9"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display text-4xl italic text-primary/35">
                      {principle.number}
                    </span>
                    <principle.icon className="size-5 text-primary" />
                  </div>
                  <h3 className="mt-14 text-2xl leading-tight">{principle.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {principle.copy}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-[0.42fr_1fr] lg:gap-20">
            <div>
              <p className="section-kicker">04 / Operating model</p>
              <h2 className="mt-5 text-4xl leading-[0.96] sm:text-5xl">
                Thoughtful before tactical
              </h2>
              <p className="mt-6 max-w-sm text-sm leading-relaxed text-muted-foreground">
                The work begins with the business context and moves toward the ad account—not the
                other way around.
              </p>
            </div>
            <div className="border-t border-border">
              {OPERATING_MODEL.map((step) => (
                <article
                  key={step.number}
                  className="grid gap-5 border-b border-border py-8 sm:grid-cols-[4rem_0.8fr_1fr] sm:items-start"
                >
                  <span className="font-display text-2xl italic text-primary/45">
                    {step.number}
                  </span>
                  <h3 className="text-xl leading-tight">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{step.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="about-cta border-t border-border">
          <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-[1fr_auto] lg:items-end lg:px-8 lg:py-24">
            <div>
              <p className="about-cta__kicker">Free review / Written reply</p>
              <h2 className="mt-5 max-w-4xl text-4xl leading-[0.95] sm:text-6xl">
                A written answer on what your listings actually need.
              </h2>
              <p className="mt-6 max-w-2xl text-sm leading-relaxed">
                For property agencies, brokers and developers in Dubai, Europe and the US. Kristian
                replies within one business day with the service that fits and its price.
              </p>
            </div>
            <div className="flex flex-col items-start gap-4 lg:items-end">
              <Button
                size="xl"
                className="border border-[#d0aa61] bg-[#d0aa61] text-[#1b1210] hover:bg-[#e0c07e]"
                asChild
              >
                <a href="/#audit">
                  Get a free review <ArrowRight className="size-4" />
                </a>
              </Button>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#f3eee4]/60 underline-offset-4 hover:text-[#f3eee4] hover:underline"
              >
                Message on WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
